<script lang="ts" setup>
	import StringMethods from '~/utils/character'
	import usePurchases from '~/services/apis/purchases-info'
	import type { ModalOption } from '~/components/Dialogs/options.vue'
	import { TimeOut } from '@/services/apis/timeout'

	type DependantPreviewInfo = {
		'Dependant Name': string
		'Phone Number': string
		'Dependant Type': string
		Gender: string
		Relationship: string
		'Hmo Id': string
	}
	type SelectedDependantContext = PolicyResponse & {
		downloading: boolean
		hmo_img: string
		preview: DependantPreviewInfo
	}

	const props = defineProps<{
		policyDetails: PolicyResponse | null
		isPendingPolicy: boolean
		isDeactivated?: boolean
	}>()

	defineEmits<{
		(event: 'activate'): void
		(event: 'reminder'): void
		(event: 'reload-policy'): void
		(event: 'new-slot'): void
	}>()

	const { custodian_id } = useRoute().params
	const { getPolicyDependants } = usePurchases()
	const {
		data,
		isLoading: fetchingDependants,
		refresh
	} = useApiLoader(
		async () =>
			await getPolicyDependants(custodian_id as string, { purchase_id: props.policyDetails?.purchase_id ?? '' }),
		{
			loadOnMount: !props.isPendingPolicy
		}
	)

	const { convertNumberToText } = StringMethods()
	const {
		canBuySlot,
		isFamilyPlan: isFamily,
		planName: productName,
		totalNoOfDependant,
		isStaging,
		policyId,
		isExpired
	} = usePolicyInfo(() => ({ policyInfo: props.policyDetails }))
	const isHmoOpen = shallowRef(false)
	const warning = shallowRef(false)
	const dependantType = shallowRef<'minor' | 'major' | ''>('')
	const dialogOption = reactive({
		addDependant: false,
		removeDependant: false,
		typesSelectorModal: false
	})

	const selectedDependant = ref<PolicyResponse | null>(null)

	const staffDependants = computed(() => (data?.value?.data || []) as PolicyResponse[])
	const {
		hmoCard: DependantHmo,
		policyAuthor: DependantInfo,
		isPolicyPending: isDependantPolicyPending
	} = usePolicyInfo(() => ({
		policyInfo: selectedDependant.value
	}))
	const totalDependantsSlot = computed(() => {
		return Number(totalNoOfDependant.value)
	})
	const totalActiveDependant = computed(
		() => staffDependants.value.filter(dependant => dependant?.is_assigned).length || 0
	)
	const totalPendingDependant = computed(
		() => staffDependants.value.filter(dependant => !dependant?.is_assigned).length || 0
	)

	const minimumFamily = FAMILYPLANMIN

	const dependantTempId = computed(() => {
		// Get the first pending depedant slot for activation
		return staffDependants.value.find(item => !item?.is_assigned)?.temp_id ?? ''
	})

	const placeholderMessages = computed(() => {
		if (props.isPendingPolicy && Boolean(totalDependantsSlot.value)) {
			return `This plan has pending slots for ${convertNumberToText(totalDependantsSlot.value)} (${
				totalDependantsSlot.value
			}) dependants, kindly activate plan to add dependant.`
		} else if (props.isPendingPolicy && !totalDependantsSlot.value) {
			return 'This plan has no dependant slots, kindly activate your plan to use.'
		} else if (totalDependantsSlot.value) {
			return `This plan has pending slots for ${convertNumberToText(totalDependantsSlot.value)} (${
				totalDependantsSlot.value
			}) dependants, kindly add dependant.`
		}
		return 'Dependant slot not available'
	})
	const dependantLabel = computed(() => {
		if (totalDependantsSlot.value === 0) {
			return 'This staff has no dependant slot'
		}
		return `${totalActiveDependant.value} of ${totalDependantsSlot.value} dependants added`
	})

	const dependantTypes = ref<ModalOption<'major' | 'minor'>[]>([
		{
			name: 'Major Dependant',
			key: 'major',
			icon: {
				dir: 'other',
				name: 'avatar',
				pathClass: 'fill-primary-2 ',
				wrapperClass: 'bg-success-50 border-success-100'
			},
			desc: 'A person who can manage their plan independently'
		},
		{
			name: 'Minor Dependant',
			key: 'minor',
			icon: {
				dir: 'other',
				name: 'group-profile',
				pathClass: 'fill-primary-2 ',
				wrapperClass: 'bg-success-50 border-success-100'
			},
			desc: 'A child or person who can not manage their plan'
		}
	])

	const handleRemoval = async (arg: PolicyResponse) => {
		if (isFamily.value) {
			const remainingDependant = staffDependants.value.filter(item => !item.to_be_removed).length
			if (remainingDependant === FAMILYPLANMIN) {
				warning.value = true
				return null
			}
		}
		selectedDependant.value = arg
		await TimeOut(100)
		dialogOption.removeDependant = true
	}
	const viewPolicy = async (arg: PolicyResponse) => {
		selectedDependant.value = arg
		await nextTick()
		if (!DependantHmo.value) {
			return useNotification().alert({
				message: 'HMO is not available at the moment.',
				status: 404
			})
		}
		isHmoOpen.value = !isHmoOpen.value
	}
	const toggleDependantForm = (key?: ModalOption<'major' | 'minor'>) => {
		dialogOption.typesSelectorModal = !dialogOption.typesSelectorModal
		dialogOption.addDependant = !dialogOption.addDependant
		dependantType.value = key?.key || ''
	}

	const navigateToRemoval = async () => {
		if (selectedDependant.value?.id) {
			await navigateTo({
				name: 'mycover-custodian-custodian_id-dependant_id',
				params: {
					id: selectedDependant.value.id,
					custodian_id: policyId.value
				},
				query: {
					policy_status: isDependantPolicyPending.value ? 'pending' : 'active'
				}
			})
			dialogOption.removeDependant = false
		}
	}
</script>

<template>
	<div class="space-y- flex flex-col justify-center">
		<my-cover-policy-label
			v-if="canBuySlot"
			:label="dependantLabel"
			:icon="{ name: 'people', dir: 'fill', pathClass: 'fill-primary-2 ' }"
		>
			<button
				v-if="!isExpired"
				type="button"
				:disabled="!!totalPendingDependant || fetchingDependants"
				class="rounded-md bg-primary-2 px-2 py-2.5 text-xs transition-all active:scale-95 disabled:scale-100 disabled:cursor-not-allowed disabled:!bg-gray-300 sm:py-3"
				data-testid="add dependant slot"
				@click="$emit('new-slot')"
			>
				New Dependant Slot
			</button>
		</my-cover-policy-label>

		<my-cover-pending-policy-placeholder
			v-if="!fetchingDependants || isPendingPolicy"
			icon-dir="fill"
			class="w-full"
			path-class="fill-gray-200"
			title="No dependant available"
			:sub-title="placeholderMessages"
			icon-name="user-add"
			:is-pending="isPendingPolicy || !totalDependantsSlot"
			:use-btn="!isDeactivated"
			use-reminder
			@activate="$emit('activate')"
			@reminder="$emit('reminder')"
		>
			<template #button-group>
				<span />
			</template>
		</my-cover-pending-policy-placeholder>

		<template v-if="!isPendingPolicy">
			<div class="my-5 max-h-[500px] overflow-y-auto">
				<loader-skeleton
					v-if="fetchingDependants"
					:height="280"
					:count="1"
					class-list="!my-5"
					:max-width="false"
				/>

				<my-cover-health-dependant-item
					v-for="(item, index) in staffDependants"
					:key="`dependant-${index + 1}`"
					:title="`Dependant ${index + 1}`"
					:policy="item"
					:loading="fetchingDependants"
					@remove="handleRemoval(item)"
					@view="viewPolicy(item)"
				/>
			</div>

			<button
				v-if="
					totalActiveDependant !== totalDependantsSlot && !isDeactivated && !fetchingDependants && !isExpired
				"
				type="button"
				data-testid="add dependant"
				class="default-btn mx-auto w-full max-w-[224px] justify-center bg-primary-2 active:scale-95"
				@click="() => (dialogOption.typesSelectorModal = true)"
			>
				Add Dependant
			</button>
		</template>

		<dialogs-options
			v-model:show="dialogOption.typesSelectorModal"
			:category-options="dependantTypes"
			paragraph="Select the dependant type"
			@proceed="toggleDependantForm"
		>
			<template #title>
				Add Dependant
				<span class="text-primary-2"
					>({{ Number(totalActiveDependant + 1) }} of {{ totalDependantsSlot }})</span
				>
			</template>
		</dialogs-options>

		<my-cover-health-add-dependant
			v-model:show="dialogOption.addDependant"
			v-model:add-dialog="dialogOption.typesSelectorModal"
			:policy-id="String(custodian_id)"
			:staff-policy="policyDetails"
			:dependant-id="dependantTempId"
			:dependant-type="dependantType"
			:total-dependant="totalDependantsSlot"
			:pending-slot="totalPendingDependant"
			:active-slot="totalActiveDependant"
			@back="toggleDependantForm"
			@reset="dialogOption.typesSelectorModal = true"
			@refresh="refresh"
		/>
		<my-cover-health-remove-dependant-modal
			v-model:show="dialogOption.removeDependant"
			:status="isDependantPolicyPending ? 'pending' : 'active'"
			:staff-name="policyDetails?.first_name || 'N/A'"
			:plan="policyDetails || null"
			:dependant-name="DependantInfo.full_name || 'N/A'"
			:loading="fetchingDependants"
			:product-name="productName"
			:staff-id="selectedDependant?.id || ''"
			@done="navigateToRemoval"
		/>
		<dialogs-error
			v-model:show="warning"
			class="items-center"
			header="Remove Staff Dependant"
			:use-cancel-btn="false"
			no-footer-btn
			close-on-bg
			modal-button-text="Okay"
			:icon-props="{ height: 40, width: 40 }"
			modal-class="max-w-[500px] sm:min-h-[0]"
			@done="warning = false"
		>
			<div class="my-5 text-balance text-center text-base font-normal leading-snug text-gray-600">
				Please be aware that <span class="font-semibold">FAMILY PLAN</span> requires a minimum of
				{{ minimumFamily }} dependant per policy.
			</div>
		</dialogs-error>

		<a-image
			:width="200"
			:style="{ display: 'none' }"
			:preview="{
				visible: isHmoOpen,
				onVisibleChange: () => (isHmoOpen = !isHmoOpen)
			}"
			:src="DependantHmo"
			loading="lazy"
		/>
	</div>
</template>
